import { z } from 'zod';
/**
 * PARTNER-PORTÁL SZEREPEK — a kapcsolattartó jogköre EGY FIÓKON. Ugyanaz a személy fiókonként MÁS szerepet
 * kaphat (a `platform.access_grants.role_id` per-erőforrás hordozza), ezért a szerep sosem „a felhasználóé",
 * hanem mindig a (felhasználó, fiók) páré.
 *
 * A három szint a legacy CRM paritásából jön:
 *   • `portal-admin`  — az ELSŐDLEGES KAPCSOLATTARTÓ utódja: a fiók minden jegye, jegy-hozzáférés osztása,
 *     a kollégák portál-szerepének kezelése (a partner-felületen), számlák, szolgáltatások.
 *   • `portal-member` — jegyet nyit és válaszol; a SAJÁT jegyeit és a neki megosztottakat látja; számlák.
 *   • `portal-viewer` — csak olvas (jegy-lista, üzenetek, számlák) — a legacyben nem volt, a „könyvelő
 *     csak a számlákat nézi" esetre.
 *
 * A kulcsok az Odin `audience=partner` szerepeivel EGYEZNEK (`seed/atlas-rbac.json`); a manifest-spec pinneli.
 */
export const PortalRoleSchema = z.enum(['portal-admin', 'portal-member', 'portal-viewer']);
/**
 * Egy sor a fiók „Portál-hozzáférés" szekciójában: a fiók egy KAPCSOLATTARTÓJA + a portál-állapota.
 * A lista a fiók kötéseiből (`clients/account-affiliations`) indul — tehát azok is látszanak, akiknek MÉG
 * NINCS portál-hozzáférésük (`role: null`), különben nem lehetne nekik adni.
 */
export const PortalAccessRowSchema = z.object({
    identityId: z.string(),
    /** `null`, ha az identity nem oldható fel (törölt személy) — a felület „Törölt felhasználó"-t mutat, nem UUID-t. */
    name: z.string().nullable(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    email: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    /** A fiók-kötés minősége (`contact` | `admin`) — a portál a KAPCSOLATTARTÓKNAK szól. */
    kind: z.enum(['admin', 'contact']),
    /** Elsődleges kapcsolattartó a fiókon (ÜZLETI jelölés — önmagában NEM ad jogot). */
    isPrimary: z.boolean(),
    /** A kötés régi „portál-hozzáférés" jelzője (a meghívás ténye) — a JOGOT a `role` adja. */
    portalAccess: z.boolean(),
    /** A HATÁLYOS portál-szerep ezen a fiókon, vagy `null` (nincs hozzáférése). */
    role: PortalRoleSchema.nullable(),
    /** Az Odin-oldali grant azonosítója (a visszavonáshoz) — `null`, ha nincs hozzáférése. */
    grantId: z.string().nullable(),
});
export const PortalAccessListResponseSchema = z.object({
    rows: z.array(PortalAccessRowSchema),
});
/** Szerep-adás/-váltás egy fiókon (idempotens upsert). */
export const PortalAccessSetBodySchema = z.object({
    role: PortalRoleSchema,
});
/** Visszavonás válasza — a friss sor (a `role`/`grantId` már `null`). */
export const PortalAccessRevokeResponseSchema = PortalAccessRowSchema;
/**
 * Egy portál-hozzáférés a SZEMÉLY nézőpontjából (a jogosultság-oldal partner-füléhez): melyik fiókon milyen
 * szerepben. Ugyanaz a személy több sorral szerepel — épp ez a lényeg: a jogkör a (személy, fiók) páré.
 */
export const PortalAccessGrantRowSchema = z.object({
    identityId: z.string(),
    accountId: z.string(),
    /**
     * `null`, ha a fiók nem oldható fel (törölt fiók) — a felület „(törölt fiók)" jelzést mutat, nem UUID-t.
     * Az ilyen sor ADATHIBA jele: a fiók-törlés kaszkádja a grantot visszavonja (azóta), tehát élő
     * grant törölt fiókon csak korábbi törlésből maradhatott.
     */
    accountName: z.string().nullable(),
    role: PortalRoleSchema,
    grantId: z.string(),
});
export const PortalAccessOverviewResponseSchema = z.object({
    grants: z.array(PortalAccessGrantRowSchema),
});
//# sourceMappingURL=schemas.js.map